import { useState, useEffect } from 'react';
import AppShell from '../components/layout/AppShell';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { studentAPI } from '../utils/api';

const BADGE_DEFINITIONS = [
  { id: 'first_topic', icon: '🌱', name: 'First Step', desc: 'Mastered your first topic', condition: (s) => s.mastered >= 1, color: '#E6F4EC', border: '#1B7F3E' },
  { id: 'ten_topics', icon: '⭐', name: 'Rising Star', desc: 'Mastered 10 topics', condition: (s) => s.mastered >= 10, color: '#FFF3E0', border: '#FF6B00' },
  { id: 'twenty_five', icon: '🔥', name: 'On Fire', desc: 'Mastered 25 topics', condition: (s) => s.mastered >= 25, color: '#FEF3C7', border: '#F59E0B' },
  { id: 'fifty_topics', icon: '💎', name: 'Diamond Learner', desc: 'Mastered 50 topics', condition: (s) => s.mastered >= 50, color: '#EFF6FF', border: '#2563EB' },
  { id: 'hundred_topics', icon: '🏆', name: 'Champion', desc: 'Mastered 100 topics', condition: (s) => s.mastered >= 100, color: '#FEF3C7', border: '#F59E0B' },
  { id: 'streak_3', icon: '🔥', name: '3-Day Streak', desc: 'Studied 3 days in a row', condition: (s) => s.streak >= 3, color: '#FEF3C7', border: '#F59E0B' },
  { id: 'streak_7', icon: '⚡', name: 'Week Warrior', desc: 'Studied 7 days in a row', condition: (s) => s.streak >= 7, color: '#FFF3E0', border: '#FF6B00' },
  { id: 'streak_30', icon: '🌟', name: 'Monthly Master', desc: 'Studied 30 days in a row', condition: (s) => s.streak >= 30, color: '#E8F0FB', border: '#1A3C6E' },
  { id: 'perfect_quiz', icon: '💯', name: 'Perfect Score', desc: 'Got 100% in a quiz', condition: (s) => s.perfectQuizzes >= 1, color: '#E6F4EC', border: '#1B7F3E' },
  { id: 'five_perfect', icon: '🎯', name: 'Sharpshooter', desc: 'Got 100% in 5 quizzes', condition: (s) => s.perfectQuizzes >= 5, color: '#FFF3E0', border: '#FF6B00' },
  { id: 'first_revision', icon: '📅', name: 'Disciplined', desc: 'Completed first revision', condition: (s) => s.revisionsCompleted >= 1, color: '#EFF6FF', border: '#2563EB' },
  { id: 'all_revisions', icon: '🎓', name: 'Guru Student', desc: 'Completed all 3 revisions for a topic', condition: (s) => s.topicsFullyRevised >= 1, color: '#E6F4EC', border: '#1B7F3E' },
];

const MILESTONES = [
  { label: '1st Topic', target: 1, key: 'mastered' },
  { label: '10 Topics', target: 10, key: 'mastered' },
  { label: '25 Topics', target: 25, key: 'mastered' },
  { label: '50 Topics', target: 50, key: 'mastered' },
  { label: '100 Topics', target: 100, key: 'mastered' },
  { label: '7-Day Streak', target: 7, key: 'streak' },
  { label: '30-Day Streak', target: 30, key: 'streak' },
  { label: '10 Revisions Done', target: 10, key: 'revisionsCompleted' },
];

const AchievementsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState({ mastered: 0, streak: 0, revisionsCompleted: 0, perfectQuizzes: 0, topicsFullyRevised: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [progressRes, streakRes] = await Promise.all([
        studentAPI.getProgress(),
        studentAPI.getStreak()
      ]);
      const p = progressRes.data.data.stats;
      const s = streakRes.data.data;
      setStats({
        mastered: p.mastered || 0,
        streak: s.current_streak || 0,
        longestStreak: s.longest_streak || 0,
        revisionsCompleted: p.revisionsCompleted || 0,
        perfectQuizzes: p.perfectQuizzes || 0,
        topicsFullyRevised: p.topicsFullyRevised || 0,
        total: p.total || 0,
        inProgress: p.inProgress || 0,
      });
    } catch {
      setStats({ mastered: 0, streak: 0, revisionsCompleted: 0, perfectQuizzes: 0, topicsFullyRevised: 0 });
    } finally {
      setLoading(false);
    }
  };

  const unlockedBadges = BADGE_DEFINITIONS.filter(b => b.condition(stats));
  const lockedBadges = BADGE_DEFINITIONS.filter(b => !b.condition(stats));

  const nextMilestone = MILESTONES.find(m => {
    const val = stats[m.key] || 0;
    return val < m.target;
  });

  return (
    <AppShell streak={stats.streak}>
      <div className="topbar">
        <div style={{ fontSize: '15px', fontWeight: 800 }}>Achievements</div>
        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{unlockedBadges.length} / {BADGE_DEFINITIONS.length} badges earned</div>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : (
          <>
            {/* Hero stats */}
            <div className="fade-up-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {[
                { icon: '🏅', val: unlockedBadges.length, lbl: 'Badges Earned', color: 'var(--brand-primary-light)' },
                { icon: '🔥', val: stats.streak, lbl: t('achievements.currentStreak'), color: 'var(--yellow-light)' },
                { icon: '⭐', val: stats.longestStreak || 0, lbl: t('achievements.longestStreak'), color: 'var(--brand-secondary-light)' },
                { icon: '🎯', val: stats.mastered, lbl: t('achievements.topicsMastered'), color: 'var(--green-light)' },
              ].map(s => (
                <div key={s.lbl} className="card" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{s.icon}</div>
                  <div>
                    <div style={{ fontSize: '22px', fontWeight: 800, lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)', fontWeight: 600, marginTop: '2px' }}>{s.lbl}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Next milestone */}
            {nextMilestone && (
              <div className="card fade-up-2" style={{ background: 'var(--brand-secondary)', padding: '16px 20px' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>{`🎯 ${t('achievements.nextMilestone')}`}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 800, color: 'white' }}>{nextMilestone.label}</div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--brand-primary)' }}>
                    {stats[nextMilestone.key] || 0} / {nextMilestone.target}
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '10px', height: '8px' }}>
                  <div style={{ background: 'var(--brand-primary)', borderRadius: '10px', height: '100%', width: `${Math.min(((stats[nextMilestone.key] || 0) / nextMilestone.target) * 100, 100)}%`, transition: 'width 0.5s' }}></div>
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '6px' }}>
                  {nextMilestone.target - (stats[nextMilestone.key] || 0)} {t('achievements.moreToGo')}
                </div>
              </div>
            )}

            {/* Earned badges */}
            {unlockedBadges.length > 0 && (
              <div className="card fade-up-3">
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px' }}>🏅 Earned Badges ({unlockedBadges.length})</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {unlockedBadges.map(badge => (
                    <div key={badge.id} style={{ background: badge.color, border: `1.5px solid ${badge.border}`, borderRadius: 'var(--radius)', padding: '14px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px' }}>{badge.icon}</div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text)', marginBottom: '3px' }}>{badge.name}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', lineHeight: 1.4 }}>{badge.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Locked badges */}
            {lockedBadges.length > 0 && (
              <div className="card fade-up-4">
                <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '14px', color: 'var(--muted)' }}>undefined</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                  {lockedBadges.map(badge => (
                    <div key={badge.id} style={{ background: 'var(--bg)', border: '1.5px dashed var(--border)', borderRadius: 'var(--radius)', padding: '14px', textAlign: 'center', opacity: 0.7 }}>
                      <div style={{ fontSize: '32px', marginBottom: '8px', filter: 'grayscale(1)' }}>{badge.icon}</div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--muted)', marginBottom: '3px' }}>{badge.name}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)', lineHeight: 1.4 }}>{badge.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state if no badges yet */}
            {unlockedBadges.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '14px' }}>🌱</div>
                <div style={{ fontSize: '16px', fontWeight: 800, marginBottom: '8px' }}>{t('achievements.journeyStarts')}</div>
                <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '0' }}>
                  Master your first topic to earn your first badge. Your Guruji believes in you! 🙏
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
};

export default AchievementsPage;
